// PresentationTab.js
import React, { useRef, useEffect } from 'react';
import { Row, Col, Typography, Select } from 'antd';
import MarkdownPreview from '@uiw/react-markdown-preview';
import StoryPresenter from './StoryPresenter';
import ImageLoader from './ImageLoader';
import { useRealmObjectContext } from '../contexts/RealmObjectProvider';
import useImageService from '../hooks/useImageServices';   // ⬅️ pull blobs like ImageLoader
import '../App.css';
import './css/PresentationTab.css';

const { Text, Title } = Typography;

const PresentationTab = ({ onRegisterCopyBuilder }) => {
  const { realmObject, realmObjectID, updateRealmObject } = useRealmObjectContext();
  const { fetchImageBlob } = useImageService();            // ⬅️ same pipeline as viewer

  const shortPreviewRef = useRef(null);
  const longPreviewRef  = useRef(null);

  const layoutOptions = [
    { value: 'full',         label: 'Full View' },
    { value: 'image-only',   label: 'Image Only' },
    { value: 'short-story',  label: 'Short Story with Image' },
    { value: 'long-story',   label: 'Long Story with Image' }
  ];
  const currentLayout = realmObject?.payload?.layout || 'full';
  const handleLayoutChange = async (value) => { await updateRealmObject('layout', value); };

  const hasImages = !!(realmObject?.payload?.images?.length);
  const noImageColSpan = 3;

  // ---------- helpers ----------
  const escapeHtml = (s = '') =>
    s.replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));

  const scrubBackgrounds = (html) => {
    const wrap = document.createElement('div');
    wrap.innerHTML = html || '';
    wrap.querySelectorAll('*').forEach((el) => {
      if (el.style && el.style.backgroundColor) el.style.backgroundColor = '';
      el.classList.remove('markdown-previewer', 'custom-editor', 'mdxeditor-root-contenteditable');
      el.style.backgroundColor = 'transparent';
    });
    return wrap.innerHTML;
  };

  // blob -> PNG data URL (scaled) + dimensions
  async function blobToPngDataURLWithSize(blob, maxWidth = 1280) {
    if (!blob) return null;
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = URL.createObjectURL(blob);
    });
    const natW = img.naturalWidth || img.width || 1;
    const natH = img.naturalHeight || img.height || 1;
    const scale = Math.min(1, maxWidth / natW);
    const w = Math.max(1, Math.round(natW * scale));
    const h = Math.max(1, Math.round(natH * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    URL.revokeObjectURL(img.src);

    const dataUrl = canvas.toDataURL('image/png');
    return { dataUrl, widthPx: w, heightPx: h };
  }

  async function buildHTMLAsync() {
  const name = realmObject?.name || 'Untitled';
  const shortHTMLRaw = shortPreviewRef.current?.innerHTML || '';
  const longHTMLRaw  = longPreviewRef.current?.innerHTML  || '';

  // Clean story fragments (strip backgrounds, classes)
  const cleanShort = scrubBackgrounds(shortHTMLRaw);
  const cleanLong  = scrubBackgrounds(longHTMLRaw);

  // Fetch the same image blob the viewer shows and embed as PNG data-URL
  let imageTag = '';
  try {
    const blob = await fetchImageBlob(realmObject?.RealmID, realmObject?.RealmObjectID);
    if (blob) {
      const packed = await blobToPngDataURLWithSize(blob, 1280);
      if (packed?.dataUrl) {
        const alt = escapeHtml(name);
        imageTag = `<p><img src="${packed.dataUrl}" alt="${alt}" style="max-width:100%;height:auto;" /></p>`;
      }
    }
  } catch { /* soft-fail: no image */ }

  // Title with explicit transparent BG so themes don’t bleed in
  const titleHTML = `<h1 style="background-color:transparent;margin:0;">${escapeHtml(name)}</h1>`;

  // Build per layout
  let bodyHTML = '';
  switch (currentLayout) {
    case 'image-only':
      bodyHTML = `${imageTag}`;
      break;
    case 'short-story':
      bodyHTML = `${imageTag}${cleanShort}`;
      break;
    case 'long-story':
      bodyHTML = `${imageTag}${cleanLong}`;
      break;
    case 'full':
    default:
      // Optional separator between short & long
      const sep = `<hr style="border:none;border-top:1px solid #ddd;margin:12px 0;" />`;
      bodyHTML = `${imageTag}${cleanShort}${sep}${cleanLong}`;
      break;
  }

  const htmlFragment = `
    <article>
      ${titleHTML}
      ${bodyHTML}
    </article>
  `.trim();

  // Plain text fallback
  const d = document.createElement('div');
  d.innerHTML = htmlFragment;
  const plain = d.innerText;

  return { htmlFragment, plain };
}

// Re-register builder when layout or object changes
useEffect(() => {
  if (typeof onRegisterCopyBuilder === 'function') {
    onRegisterCopyBuilder(() => buildHTMLAsync());
  }
}, [onRegisterCopyBuilder, realmObject, currentLayout]);

  // ---------- /helpers ----------

  return (
    <div className="tab-container">
      <Row gutter={[8, 8]}>
        <Col span={24}>
          <div className="layout-input-container">
            <Title level={5} className="header-title">Layout</Title>
            <Select
              options={layoutOptions}
              value={currentLayout}
              onChange={handleLayoutChange}
              className="layout-select"
              popupClassName="small-select"
              style={{ width: 160 }}
            />
          </div>
        </Col>
      </Row>

      <Row gutter={[8, 8]}>
        {(currentLayout === 'full' || currentLayout === 'image-only' || currentLayout === 'short-story' || currentLayout === 'long-story') && (
          <Col xs={24} sm={24} md={hasImages ? 12 : noImageColSpan} lg={hasImages ? 12 : noImageColSpan}>
            {realmObject ? (
              <ImageLoader
                key={realmObjectID}
                /* your existing handlers */
              />
            ) : (
              <Text type="warning">Unable to Load Images for this object...</Text>
            )}
          </Col>
        )}

        {(currentLayout === 'full' || currentLayout === 'short-story') && (
          <Col xs={24} sm={24} md={hasImages ? 12 : 24 - noImageColSpan} lg={hasImages ? 12 : 24 - noImageColSpan}>
            <StoryPresenter fieldKey="ShortStory" StoryLabel={`Brief Story about ${realmObject?.name}`} storySize="old-story" />
          </Col>
        )}

        {(currentLayout === 'full' || currentLayout === 'long-story') && (
          <Col span={24}>
            <StoryPresenter fieldKey="LongStory" StoryLabel={`Details about ${realmObject?.name}`} storySize="large-story" />
          </Col>
        )}
      </Row>

      {/* Hidden previews used ONLY for copy-as-HTML */}
      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0 }} aria-hidden="true">
        <div ref={shortPreviewRef} className="copy-clean">
          <MarkdownPreview source={realmObject?.payload?.ShortStory || ''} />
        </div>
        <div ref={longPreviewRef} className="copy-clean">
          <MarkdownPreview source={realmObject?.payload?.LongStory || ''} />
        </div>
      </div>
    </div>
  );
};

export default PresentationTab;
